import React, { useState, useRef, useEffect } from 'react';

// Interface for CSV data popup props
export interface CsvDataPopupProps {
  isOpen: boolean;
  onClose: () => void;
  csvData: any[]; // Array of CSV items
  onApplyMapping: (mapping: { [key: string]: string | string[] }) => void;
  sourceType?: 'csv' | 'pdf'; // Type of source data
}

// Target fields for Purchase table (order matches purchase table: Medicine Name, Brand Name, Composition Name, HSN Code, Batch Number, Storage Location, Expiry Date, Quantity, Quantity Type, Per Quantity, Free, Cost Price, MRP, Discount, GST Rate, Total Amount)
const TARGET_FIELDS = [
  { key: 'Medicine Name', label: 'Medicine Name' },
  { key: 'Brand Name', label: 'Brand Name' },
  { key: 'Composition Name', label: 'Composition Name' },
  { key: 'HSN Code', label: 'HSN Code' },
  { key: 'Batch Number', label: 'Batch Number' },
  { key: 'storage_location', label: 'Storage Location' },
  { key: 'Expiry Date', label: 'Expiry Date' },
  { key: 'Quantity', label: 'Quantity' },
  { key: 'Quantity Type', label: 'Quantity Type' },
  { key: 'Tablets Per Strip', label: 'Per Quantity' },
  { key: 'Free', label: 'Free' },
  { key: 'Cost Price', label: 'Cost Price' },
  { key: 'MRP', label: 'MRP' },
  { key: 'Discount', label: 'Discount (%)' },
  { key: 'GST Rate', label: 'GST Rate (Drag all tax fields here)' },
  { key: 'Total Amount', label: 'Total Amount' }
];

const CsvDataPopup: React.FC<CsvDataPopupProps> = ({
  isOpen,
  onClose,
  csvData,
  onApplyMapping,
  sourceType = 'csv',
}) => {
  const [csvPreviewMode, setCsvPreviewMode] = useState<'columns' | 'preview'>('columns');
  const [fieldMapping, setFieldMapping] = useState<{ [key: string]: string | string[] }>({});
  const [tempFieldMapping, setTempFieldMapping] = useState<{ [key: string]: string | string[] }>({});
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const csvHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const csvBodyScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // CSV table scroll handlers
  const handleCsvHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    if (csvBodyScrollRef.current && csvBodyScrollRef.current.scrollLeft !== scrollLeft) {
      csvBodyScrollRef.current.scrollLeft = scrollLeft;
    }
  };

  const handleCsvBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    if (csvHeaderScrollRef.current && csvHeaderScrollRef.current.scrollLeft !== scrollLeft) {
      csvHeaderScrollRef.current.scrollLeft = scrollLeft;
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, fieldName: string) => {
    setDraggedField(fieldName);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldName);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, fieldName: string) => {
    if (isMobile) {
      const touch = e.touches[0];
      setTouchStartPos({ x: touch.clientX, y: touch.clientY });
      setDraggedField(fieldName);
      setIsTouchDragging(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isMobile && touchStartPos && draggedField) {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.y);
      
      if (deltaX > 10 || deltaY > 10) {
        setIsTouchDragging(true);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile && draggedField) {
      const touch = e.changedTouches[0];
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (elementBelow) {
        const dropTarget = elementBelow.closest('[data-drop-target]');
        if (dropTarget) {
          const targetField = dropTarget.getAttribute('data-drop-target');
          if (targetField) {
            setTempFieldMapping(prev => {
              const existingMapping = prev[targetField];
              
              // Only allow multiple mappings for GST Rate field
              if (targetField === 'GST Rate') {
                // If already mapped, convert to array or append to array
                if (existingMapping) {
                  const existingArray = Array.isArray(existingMapping) ? existingMapping : [existingMapping];
                  // Don't add if already in the array
                  if (!existingArray.includes(draggedField)) {
                    return {
                      ...prev,
                      [targetField]: [...existingArray, draggedField]
                    };
                  }
                  return prev;
                } else {
                  // First mapping - start with single string
                  return {
                    ...prev,
                    [targetField]: draggedField
                  };
                }
              } else {
                // For all other fields, replace existing mapping (single field only)
                return {
                  ...prev,
                  [targetField]: draggedField
                };
              }
            });
          }
        }
      }
      
      setDraggedField(null);
      setTouchStartPos(null);
      setIsTouchDragging(false);
    }
  };

  const handleFieldDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFieldDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    const draggedFieldName = e.dataTransfer.getData('text/plain');
    
    if (draggedFieldName && targetField) {
      setTempFieldMapping(prev => {
        const existingMapping = prev[targetField];
        
        // Only allow multiple mappings for GST Rate field
        if (targetField === 'GST Rate') {
          // If already mapped, convert to array or append to array
          if (existingMapping) {
            const existingArray = Array.isArray(existingMapping) ? existingMapping : [existingMapping];
            // Don't add if already in the array
            if (!existingArray.includes(draggedFieldName)) {
              return {
                ...prev,
                [targetField]: [...existingArray, draggedFieldName]
              };
            }
            return prev;
          } else {
            // First mapping - start with single string
            return {
              ...prev,
              [targetField]: draggedFieldName
            };
          }
        } else {
          // For all other fields, replace existing mapping (single field only)
          return {
            ...prev,
            [targetField]: draggedFieldName
          };
        }
      });
    }
    
    setDraggedField(null);
    setIsDragging(false);
  };

  const clearFieldMapping = (targetField: string) => {
    setTempFieldMapping(prev => {
      const newMapping = { ...prev };
      delete newMapping[targetField];
      return newMapping;
    });
  };

  const removeFieldFromMapping = (targetField: string, fieldToRemove: string) => {
    setTempFieldMapping(prev => {
      const existingMapping = prev[targetField];
      if (!existingMapping) return prev;
      
      if (Array.isArray(existingMapping)) {
        const filtered = existingMapping.filter(f => f !== fieldToRemove);
        if (filtered.length === 0) {
          const newMapping = { ...prev };
          delete newMapping[targetField];
          return newMapping;
        } else if (filtered.length === 1) {
          // Convert back to single string if only one remains
          return {
            ...prev,
            [targetField]: filtered[0]
          };
        } else {
          return {
            ...prev,
            [targetField]: filtered
          };
        }
      } else {
        // Single field - remove it
        const newMapping = { ...prev };
        delete newMapping[targetField];
        return newMapping;
      }
    });
  };

  // Calculate sum for numeric fields (like GST)
  const calculateSum = (targetField: string, fields: string | string[]): number => {
    const fieldArray = Array.isArray(fields) ? fields : [fields];
    let sum = 0;
    
    if (csvData && csvData.length > 0) {
      // Calculate sum from first row as preview
      const firstRow = csvData[0];
      fieldArray.forEach(field => {
        const value = firstRow[field];
        if (value !== undefined && value !== null) {
          const numValue = parseFloat(String(value)) || 0;
          sum += numValue;
        }
      });
    }
    
    return sum;
  };

  // Check if a field is numeric (for sum calculation)
  const isNumericField = (targetField: string): boolean => {
    const numericFields = ['GST Rate', 'Amount', 'Discount', 'Total Amount', 'MRP', 'Quantity', 'Per Quantity', 'Free'];
    return numericFields.includes(targetField);
  };

  const clearAllFieldMappings = () => {
    const hasMappings = Object.keys(fieldMapping).length > 0 || Object.keys(tempFieldMapping).length > 0;
    
    if (hasMappings) {
      const confirmed = window.confirm(
        'Are you sure you want to clear all field mappings? This action cannot be undone.'
      );
      
      if (confirmed) {
        setFieldMapping({});
        setTempFieldMapping({});
      }
    }
  };

  const handleApply = () => {
    const finalMapping = { ...fieldMapping, ...tempFieldMapping };
    onApplyMapping(finalMapping);
    setFieldMapping(finalMapping);
    setTempFieldMapping({});
    onClose();
  };

  if (!isOpen || !csvData || csvData.length === 0) {
    return null;
  }

  const csvFields = csvData.length > 0 ? Object.keys(csvData[0]) : [];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(2,6,23,0.55)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '0.5rem' : '0',
      animation: 'fadeIn 180ms ease-out'
    }}>
      <div style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.98) 100%)',
        borderRadius: isMobile ? '12px' : '0px',
        width: isMobile ? 'calc(100vw - 1rem)' : '100vw',
        height: isMobile ? 'calc(100vh - 1rem)' : '100vh',
        maxWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid rgba(226,232,240,0.7)',
        boxShadow: isMobile ? '0 20px 40px rgba(2, 6, 23, 0.25)' : '0 30px 80px rgba(2, 6, 23, 0.35)',
        transform: 'translateY(8px)',
        animation: 'slideInUp 260ms cubic-bezier(0.22,1,0.36,1) forwards'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: isMobile ? '1rem' : '1.25rem 1.5rem',
          borderBottom: '1px solid rgba(226,232,240,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'white',
          color: '#1f2937',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? '0.75rem' : '1rem'
        }}>
          <div style={{ 
            fontWeight: 700, 
            letterSpacing: '-0.025em', 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '0.5rem' : '0.75rem',
            fontSize: isMobile ? '1rem' : '1.125rem',
            color: '#111827'
          }}>
            <div style={{
              width: isMobile ? '32px' : '36px', 
              height: isMobile ? '32px' : '36px', 
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
            }}>
              <svg width={isMobile ? "16" : "18"} height={isMobile ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M3 7h18M3 12h18M3 17h18"/>
              </svg>
            </div>
            <span style={{ display: isMobile ? 'none' : 'inline' }}>CSV Field Mapping</span>
            <span style={{ display: isMobile ? 'inline' : 'none' }}>CSV Mapping</span>
          </div>
          
          {/* Switch Buttons */}
          <div style={{ 
            position: 'relative',
            display: 'flex',
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '4px',
            border: '1px solid #e2e8f0',
            minWidth: isMobile ? '160px' : '180px'
          }}>
            <div style={{
              position: 'absolute',
              top: '4px',
              left: csvPreviewMode === 'columns' ? '4px' : '50%',
              width: 'calc(50% - 4px)',
              height: 'calc(100% - 8px)',
              background: 'white',
              borderRadius: '8px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              zIndex: 1
            }} />
            
            <button
              onClick={() => setCsvPreviewMode('columns')}
              style={{
                position: 'relative',
                zIndex: 2,
                background: 'transparent',
                color: csvPreviewMode === 'columns' ? '#1f2937' : '#6b7280',
                border: 'none',
                padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flex: 1,
                textAlign: 'center'
              }}
            >Map Fields</button>
            <button
              onClick={() => setCsvPreviewMode('preview')}
              style={{
                position: 'relative',
                zIndex: 2,
                background: 'transparent',
                color: csvPreviewMode === 'preview' ? '#1f2937' : '#6b7280',
                border: 'none',
                padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flex: 1,
                textAlign: 'center'
              }}
            >Data Preview</button>
          </div>
          
          {/* Clear All Button */}
          {(Object.keys(fieldMapping).length > 0 || Object.keys(tempFieldMapping).length > 0) && (
            <button
              onClick={clearAllFieldMappings}
              style={{
                background: '#fef2f2',
                color: '#dc2626',
                border: '1px solid #fecaca',
                padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background = '#fee2e2';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.15)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(220, 38, 38, 0.1)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
              </svg>
              <span style={{ display: isMobile ? 'none' : 'inline' }}>Clear All</span>
            </button>
          )}
          
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={onClose}
              style={{
                background: 'white',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: isMobile ? '0.875rem' : '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                minWidth: isMobile ? '80px' : '100px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#9ca3af';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18"/>
              </svg>
              Cancel
            </button>
            <button
              onClick={handleApply}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: isMobile ? '0.875rem' : '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                minWidth: isMobile ? '80px' : '100px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.35)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
              </svg>
              Done
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: csvPreviewMode === 'preview' ? '1fr' : '1fr 1fr',
          gridTemplateRows: csvPreviewMode === 'preview' ? '1fr auto' : '1fr',
          flex: 1,
          minHeight: 0,
          background: 'transparent',
          gap: csvPreviewMode === 'preview' ? '1rem' : isMobile ? '0.25rem' : '0',
          height: '100%',
          paddingTop: isMobile ? '1rem' : '1.5rem',
          overflow: csvPreviewMode === 'preview' ? 'visible' : 'visible'
        }}>
          {/* Left: CSV Fields (draggable) */}
          {csvPreviewMode === 'columns' && (
            <div style={{
              borderRight: isMobile ? '1px solid rgba(226,232,240,0.4)' : '1px solid rgba(226,232,240,0.8)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'linear-gradient(180deg, rgba(16,185,129,0.03) 0%, rgba(5,150,105,0.02) 100%)',
              overflow: 'hidden',
              height: '100%'
            }}>
              <div style={{ 
                padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', 
                color: '#111827', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                gap: '0.5rem',
                fontSize: isMobile ? '0.8rem' : '1rem',
                flexShrink: 0
              }}>
                <div style={{ fontWeight: 900, letterSpacing: '0.2px' }}>CSV Fields</div>
              </div>
              <div style={{ 
                overflow: 'auto', 
                padding: isMobile ? '0 0.5rem 0.5rem 0.5rem' : '0 1rem 1rem 1rem',
                flex: 1,
                minHeight: 0
              }}>
                {csvFields.map((fieldName) => {
                  // Check if this field is already mapped (handle both string and array)
                  const isMappedInTemp = Object.values(tempFieldMapping).some(v => 
                    Array.isArray(v) ? v.includes(fieldName) : v === fieldName
                  );
                  const isMappedInSaved = Object.values(fieldMapping).some(v => 
                    Array.isArray(v) ? v.includes(fieldName) : v === fieldName
                  );
                  const isMapped = isMappedInTemp || isMappedInSaved;
                  
                  // Find which target field this is mapped to
                  let mappedToField = null;
                  if (isMapped) {
                    const targetKey = Object.keys(tempFieldMapping).find(k => {
                      const val = tempFieldMapping[k];
                      return Array.isArray(val) ? val.includes(fieldName) : val === fieldName;
                    }) || Object.keys(fieldMapping).find(k => {
                      const val = fieldMapping[k];
                      return Array.isArray(val) ? val.includes(fieldName) : val === fieldName;
                    });
                    if (targetKey) {
                      const targetField = TARGET_FIELDS.find(f => f.key === targetKey);
                      mappedToField = targetField ? targetField.label : targetKey;
                    }
                  }
                  
                  return (
                    <div
                      key={fieldName}
                      draggable
                      onDragStart={(e) => handleDragStart(e as any, fieldName)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, fieldName)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      style={{
                        cursor: 'grab',
                        padding: isMobile ? '0.5rem 0.6rem' : '0.8rem 1rem',
                        marginBottom: isMobile ? '0.4rem' : '0.75rem',
                        border: isMapped 
                          ? '2px solid rgba(16, 185, 129, 0.4)' 
                          : '2px solid rgba(59, 130, 246, 0.15)',
                        borderRadius: isMobile ? '6px' : '12px',
                        background: isMapped
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.12) 100%)'
                          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(37, 99, 235, 0.05) 100%)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: isMapped 
                          ? '0 4px 12px rgba(16, 185, 129, 0.15)' 
                          : '0 4px 12px rgba(59, 130, 246, 0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '0.3rem' : '0',
                        transform: isTouchDragging && draggedField === fieldName ? 'scale(1.05)' : 'scale(1)',
                        zIndex: isTouchDragging && draggedField === fieldName ? 1000 : 'auto',
                        touchAction: isMobile ? 'none' : 'auto',
                        opacity: isMapped ? 0.9 : 1
                      }}
                      onMouseEnter={(e) => { 
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; 
                        e.currentTarget.style.boxShadow = isMapped 
                          ? '0 8px 20px rgba(16, 185, 129, 0.2)' 
                          : '0 8px 20px rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.borderColor = isMapped 
                          ? 'rgba(16, 185, 129, 0.5)' 
                          : 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => { 
                        e.currentTarget.style.transform = 'translateY(0) scale(1)'; 
                        e.currentTarget.style.boxShadow = isMapped 
                          ? '0 4px 12px rgba(16, 185, 129, 0.15)' 
                          : '0 4px 12px rgba(59, 130, 246, 0.08)';
                        e.currentTarget.style.borderColor = isMapped 
                          ? 'rgba(16, 185, 129, 0.4)' 
                          : 'rgba(59, 130, 246, 0.15)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.4rem' : '0.75rem', position: 'relative', zIndex: 1 }}>
                        <div style={{
                          width: isMobile ? '20px' : '32px',
                          height: isMobile ? '20px' : '32px',
                          borderRadius: isMobile ? '4px' : '8px',
                          background: isMapped
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: isMobile ? '0.6rem' : '0.8rem',
                          fontWeight: 'bold',
                          boxShadow: isMapped 
                            ? '0 2px 8px rgba(16, 185, 129, 0.3)' 
                            : '0 2px 8px rgba(59, 130, 246, 0.3)',
                          flexShrink: 0
                        }}>
                          {isMapped ? '✓' : '📊'}
                        </div>
                        
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ 
                            fontWeight: 700, 
                            color: isMapped ? '#059669' : '#1e40af',
                            fontSize: isMobile ? '0.7rem' : '0.9rem',
                            marginBottom: '0.2rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {isMapped && (
                              <span style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '4px',
                                fontSize: isMobile ? '0.5rem' : '0.6rem',
                                fontWeight: '700',
                                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                                whiteSpace: 'nowrap'
                              }}>
                                MATCHED
                              </span>
                            )}
                          </div>
                          <div style={{
                            fontSize: isMobile ? '0.6rem' : '0.75rem',
                            color: isMapped ? '#059669' : '#64748b',
                            fontWeight: 500
                          }}>
                            {isMapped 
                              ? `✓ Mapped to: ${mappedToField || 'a field'}` 
                              : (sourceType === 'pdf' ? 'PDF Field' : 'CSV Field')}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: isMobile ? '0.3rem' : '0.5rem',
                        color: isMapped ? '#10b981' : '#3b82f6',
                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                        fontWeight: 600,
                        position: 'relative',
                        zIndex: 1,
                        flexShrink: 0
                      }}>
                        {!isMapped && (
                          <>
                            <span style={{ display: isMobile ? 'none' : 'inline' }}>Drag to map</span>
                            <span style={{ display: isMobile ? 'inline' : 'none' }}>{isTouchDragging && draggedField === fieldName ? 'Release to drop' : 'Touch & drag'}</span>
                          </>
                        )}
                        {isMapped && (
                          <span style={{ display: isMobile ? 'none' : 'inline' }}>Matched</span>
                        )}
                        <div style={{
                          width: isMobile ? '16px' : '20px',
                          height: isMobile ? '16px' : '20px',
                          borderRadius: '50%',
                          background: isMapped
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: isMobile ? '0.6rem' : '0.7rem',
                          fontWeight: 'bold',
                          boxShadow: isMapped 
                            ? '0 2px 6px rgba(16, 185, 129, 0.4)' 
                            : '0 2px 6px rgba(59, 130, 246, 0.4)'
                        }}>
                          {isMapped ? '✓' : '↔'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Right: Target Fields (drop zones) */}
          {csvPreviewMode === 'columns' && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'linear-gradient(180deg, rgba(16,185,129,0.03) 0%, rgba(5,150,105,0.02) 100%)',
              overflow: 'hidden',
              height: '100%'
            }}>
              <div style={{ 
                padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', 
                color: '#111827', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                gap: '0.5rem',
                fontSize: isMobile ? '0.8rem' : '1rem',
                flexShrink: 0
              }}>
                <div style={{ fontWeight: 900, letterSpacing: '0.2px' }}>Purchase Table Fields</div>
              </div>
              <div style={{ 
                overflow: 'auto', 
                padding: isMobile ? '0 0.5rem 0.5rem 0.5rem' : '0 1rem 1rem 1rem',
                flex: 1,
                minHeight: 0
              }}>
                {TARGET_FIELDS.map((field) => {
                  const mappedField = tempFieldMapping[field.key] || fieldMapping[field.key];
                  const mappedFieldsArray = mappedField 
                    ? (Array.isArray(mappedField) ? mappedField : [mappedField])
                    : [];
                  const hasMultipleFields = Array.isArray(mappedField) && mappedField.length > 1;
                  const calculatedSum = mappedField && isNumericField(field.key) 
                    ? calculateSum(field.key, mappedField) 
                    : null;
                  
                  return (
                    <div
                      key={field.key}
                      data-drop-target={field.key}
                      onDragOver={handleFieldDragOver}
                      onDrop={(e) => handleFieldDrop(e, field.key)}
                      style={{
                        padding: isMobile ? '0.5rem 0.6rem' : '0.8rem 1rem',
                        marginBottom: isMobile ? '0.4rem' : '0.75rem',
                        border: mappedField ? '2px solid rgba(16, 185, 129, 0.4)' : '2px dashed rgba(16, 185, 129, 0.2)',
                        borderRadius: isMobile ? '6px' : '12px',
                        background: mappedField 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.12) 100%)'
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, rgba(5, 150, 105, 0.04) 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: mappedField ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 6px rgba(16, 185, 129, 0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        minHeight: isMobile ? '60px' : '80px'
                      }}
                      onMouseEnter={(e) => {
                        if (!mappedField) {
                          e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.08) 100%)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!mappedField) {
                          e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, rgba(5, 150, 105, 0.04) 100%)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.4rem' : '0.75rem', flex: 1, marginBottom: mappedFieldsArray.length > 0 ? '0.5rem' : '0' }}>
                        <div style={{
                          width: isMobile ? '20px' : '32px',
                          height: isMobile ? '20px' : '32px',
                          borderRadius: isMobile ? '4px' : '8px',
                          background: mappedField 
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.3) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: isMobile ? '0.6rem' : '0.8rem',
                          fontWeight: 'bold',
                          boxShadow: mappedField ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
                          flexShrink: 0
                        }}>
                          {mappedField ? (hasMultipleFields ? '📊' : '✓') : '📋'}
                        </div>
                        
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ 
                            fontWeight: 700, 
                            color: mappedField ? '#059669' : '#1f2937',
                            fontSize: isMobile ? '0.7rem' : '0.9rem',
                            marginBottom: '0.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {field.label}
                            {hasMultipleFields && (
                              <span style={{
                                background: 'rgba(59, 130, 246, 0.15)',
                                color: '#2563eb',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '4px',
                                fontSize: isMobile ? '0.5rem' : '0.6rem',
                                fontWeight: '700'
                              }}>
                                {mappedFieldsArray.length} fields
                              </span>
                            )}
                          </div>
                          {mappedFieldsArray.length > 0 && (
                            <div style={{
                              fontSize: isMobile ? '0.55rem' : '0.65rem',
                              color: '#6b7280',
                              fontWeight: 500,
                              fontStyle: 'italic',
                              marginBottom: '0.2rem'
                            }}>
                              ({mappedFieldsArray.map(f => f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ')})
                            </div>
                          )}
                          {mappedFieldsArray.length > 0 ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                            }}>
                              {mappedFieldsArray.map((mappedFieldName, idx) => (
                                <div key={idx} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: isMobile ? '0.6rem' : '0.75rem',
                                  color: '#059669',
                                  fontWeight: 600
                                }}>
                                  <span>← {mappedFieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                  {hasMultipleFields && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeFieldFromMapping(field.key, mappedFieldName);
                                      }}
                                      style={{
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        color: '#dc2626',
                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                        borderRadius: '4px',
                                        padding: '0.125rem 0.25rem',
                                        cursor: 'pointer',
                                        fontSize: '0.5rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        lineHeight: 1
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                              {calculatedSum !== null && (
                                <div style={{
                                  marginTop: '0.25rem',
                                  padding: '0.25rem 0.5rem',
                                  background: 'rgba(59, 130, 246, 0.1)',
                                  borderRadius: '6px',
                                  fontSize: isMobile ? '0.65rem' : '0.75rem',
                                  fontWeight: 700,
                                  color: '#2563eb',
                                  display: 'inline-block',
                                  width: 'fit-content'
                                }}>
                                  Sum: {calculatedSum.toFixed(2)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{
                              fontSize: isMobile ? '0.6rem' : '0.75rem',
                              color: '#64748b',
                              fontWeight: 500
                            }}>
                              {field.key === 'GST Rate' 
                                ? `Drop ${sourceType === 'pdf' ? 'PDF' : 'CSV'} field(s) here (multiple allowed)`
                                : `Drop ${sourceType === 'pdf' ? 'PDF' : 'CSV'} field here`}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {mappedField && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
                          <button
                            onClick={() => clearFieldMapping(field.key)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#dc2626',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '6px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              fontSize: isMobile ? '0.6rem' : '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6L18 18"/>
                            </svg>
                            Clear All
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data Preview Mode */}
          {csvPreviewMode === 'preview' && (
            <div style={{ 
              padding: '0 1rem 0 1rem', 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: 0
            }}>
              <div 
                ref={csvBodyScrollRef}
                onScroll={handleCsvBodyScroll}
                style={{
                  background: 'white',
                  border: '2px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  overflow: 'auto',
                  maxHeight: isMobile ? '400px' : 'calc(35vh)',
                  height: isMobile ? '400px' : 'calc(35vh)',
                  WebkitOverflowScrolling: 'touch' as any,
                  width: '100%',
                  position: 'relative',
                  padding: '0',
                  margin: '0'
                }}
              >
                {/* Table Header */}
                <div 
                  ref={csvHeaderScrollRef}
                  onScroll={handleCsvHeaderScroll}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: csvFields.map(() => '240px').join(' '),
                    gap: '12px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    padding: '0.75rem 0.8rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                    borderRadius: '8px 8px 0 0',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    minWidth: `${csvFields.length * 240 + (csvFields.length - 1) * 12 + 32}px`
                  }}
                >
                  {csvFields.map((field) => (
                    <div
                      key={field}
                      style={{ 
                        minWidth: '240px', 
                        padding: '0.5rem',
                        borderRadius: '8px'
                      }}
                    >
                      {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                  ))}
                </div>
                
                {/* Table Body */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: csvFields.map(() => '240px').join(' '),
                  gap: '12px',
                  padding: '0.75rem 0.8rem',
                  minWidth: `${csvFields.length * 240 + (csvFields.length - 1) * 12 + 32}px`
                }}>
                  {csvData.slice(0, 10).map((row, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                      {csvFields.map((field) => (
                        <div
                          key={`${rowIndex}-${field}`}
                          style={{
                            minWidth: '240px',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            color: '#374151',
                            borderBottom: rowIndex < 9 ? '1px solid #e5e7eb' : 'none'
                          }}
                        >
                          {String(row[field] || '').substring(0, 50)}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvDataPopup;

